import type { components } from "./api-types";
import { describeHttpError } from "./httpErrors";
import { supabase } from "./supabase";

/**
 * Every type here comes from the generated OpenAPI schema, so an API shape change the UI has
 * not caught up with fails `tsc` rather than the browser (§9 Phase 2).
 */
type S = components["schemas"];

export type LeadRow = S["LeadRow"];
export type LeadPage = S["LeadPage"];
export type LeadDetail = S["LeadDetail"];
export type LeadUpdate = S["LeadUpdate"];
export type ScField = S["ScField"];
export type MessageRow = S["MessageRow"];
export type NoteRow = S["NoteRow"];
export type OfferRow = S["OfferRow"];
// v2.3 P1.1 (D-74).
export type OfferCreate = S["OfferCreate"];
export type OfferPatch = S["OfferPatch"];
export type OfferStatus = NonNullable<OfferCreate["status"]>;
export type OfferChannel = NonNullable<OfferCreate["channel"]>;
export type SuggestionFieldApply = S["SuggestionFieldApply"];
export type TaskRow = S["TaskRow"];
export type EventRow = S["EventRow"];
export type ParcelRow = S["ParcelRow"];
export type Overview = S["Overview"];
export type OverviewItem = S["OverviewItem"];
export type CallTodayRow = S["CallTodayRow"];
export type RulesDryRun = S["RulesDryRun"];
export type RuleCandidate = S["RuleCandidate"];
export type RuleActionRow = S["RuleActionRow"];
export type NavCounts = S["NavCounts"];
export type SearchHit = S["SearchHit"];
export type TaskCreate = S["TaskCreate"];
export type TaskUpdate = S["TaskUpdate"];
export type NoteCreate = S["NoteCreate"];
export type NoteUpdate = S["NoteUpdate"];
export type MeResponse = S["MeResponse"];
export type LeadStage = LeadRow["stage"];

export type DealRow = S["DealRow"];
export type DealPage = S["DealPage"];
export type DealDetail = S["DealDetail"];
export type DealUpdate = S["DealUpdate"];
export type DealStage = DealRow["stage"];
export type DealStrategy = DealRow["strategy"];
export type ChecklistItemRow = S["ChecklistItemRow"];
export type ChecklistUpdate = S["ChecklistUpdate"];
export type KillCheck = S["KillCheck"];
export type UnderwritingDerived = S["UnderwritingDerived"];
export type EngagementRow = S["EngagementRow"];
export type EngagementStatus = EngagementRow["status"];
export type EngagementCreate = S["EngagementCreate"];
export type EngagementUpdate = S["EngagementUpdate"];
export type VendorRow = S["VendorRow"];
export type VendorUpsert = S["VendorUpsert"];
export type InvestorRow = S["InvestorRow"];
export type BuyerRow = S["BuyerRow"];
export type InvestorUpsert = S["InvestorUpsert"];
export type BuyerUpsert = S["BuyerUpsert"];
export type DocumentRow = S["DocumentRow"];
export type FundingRow = S["FundingRow"];
export type DispositionRow = S["DispositionRow"];
export type PromoteRequest = S["PromoteRequest"];
export type UploadUrlRequest = S["UploadUrlRequest"];
export type UploadUrlResponse = S["UploadUrlResponse"];
export type DocumentCreate = S["DocumentCreate"];
export type ParcelDiligenceUpdate = S["ParcelDiligenceUpdate"];
export type Metrics = S["Metrics"];
export type Kpi = S["Kpi"];
export type StageSlice = S["StageSlice"];
export type DayPoint = S["DayPoint"];
export type TierBand = S["TierBand"];
export type AttributionRow = S["AttributionRow"];
export type EventPage = S["EventPage"];
export type ActivityEvent = S["ActivityEvent"];
export type AgentRunPage = S["AgentRunPage"];
export type AgentRunRow = S["AgentRunRow"];
export type IngestLogRow = S["IngestLogRow"];
export type UnmappedKey = S["UnmappedKey"];
export type IngestResponse = S["IngestResponse"];
export type IngestLogPage = S["IngestLogPage"];
export type SuggestionResponse = S["SuggestionResponse"];
export type AgentBudget = S["AgentBudget"];
export type JobRunResponse = S["JobRunResponse"];
export type AskResponse = S["AskResponse"];
export type BulkTriageResponse = S["BulkTriageResponse"];
export type MapKeyRequest = S["MapKeyRequest"];
export type LeadEdit = S["LeadEdit"];
export type ConflictChip = S["ConflictChip"];
export type ConflictResolution = S["ConflictResolution"];
export type AutoApplication = S["AutoApplication"];
export type TrustStanding = S["TrustStanding"];
export type AcceptAllResponse = S["AcceptAllResponse"];
export type DeadReason = NonNullable<LeadRow["dead_reason"]>;
export type SuggestionState = LeadRow["suggestion_state"];

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

export class ApiError extends Error {
  readonly status: number;
  /** v2.2 P1.3: the `X-Error-Id` of a 5xx — the api_errors row /system finds by ref. */
  readonly errorId: string | null;
  constructor(status: number, message: string, errorId: string | null = null) {
    super(message);
    this.status = status;
    this.errorId = errorId;
    this.name = "ApiError";
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Exported for `web/src/curative/api.ts` (v2.0, D-37).
 *
 * The Curative module keeps its own client so its surface stays inside `web/src/curative/`, but auth,
 * the error shape and the base URL are not things to have two implementations of — the second one
 * drifts, and the symptom is a module that silently stops sending the bearer token. So this is
 * exported rather than copied. Additive: no existing caller changes.
 */
export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>(path, init);
}

/**
 * The same authenticated fetch, returning the body as TEXT.
 *
 * For endpoints that answer CSV rather than JSON — v2.0's board export. A top-level navigation to
 * such a URL cannot carry the bearer header and answers 401, so the file has to be fetched and turned
 * into a blob by the caller.
 */
export async function apiText(path: string, init?: RequestInit): Promise<string> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...(await authHeader()), ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const errorId = res.headers.get("X-Error-Id");
    throw new ApiError(res.status, describeHttpError(res.status, null, errorId), errorId);
  }
  return res.text();
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // A multipart upload must NOT carry a hand-written Content-Type: the boundary is generated by the
  // browser when it serialises the FormData, and a header without it makes the server read the whole
  // body as one malformed part. v2.0's import wizard posts the file this way.
  const isForm = typeof FormData !== "undefined" && init?.body instanceof FormData;
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(isForm ? {} : { "Content-Type": "application/json" }),
      ...(await authHeader()),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail: string | null = null;
    try {
      const body = (await res.json()) as { detail?: unknown };
      if (typeof body.detail === "string") detail = body.detail;
      else if (Array.isArray(body.detail)) detail = JSON.stringify(body.detail);
    } catch {
      /* the body was not JSON; the status is all we have */
    }
    const errorId = res.headers.get("X-Error-Id");
    throw new ApiError(res.status, describeHttpError(res.status, detail, errorId), errorId);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface LeadQuery {
  q?: string;
  stage?: string[];
  tier?: string[];
  market?: string[];
  county?: string[];
  property_type?: string[];
  reply_class?: string[];
  campaign?: string;
  wave?: string;
  stale?: boolean;
  overdue?: boolean;
  has_suggestion?: boolean;
  dead_reason?: string[];
  suggestion_state?: string[];
  /** v2.2 P3.1: true lists hot leads only. */
  hot?: boolean;
  /** v2.9 P3.3: the call log's queues. */
  call_list?: "callbacks_due" | "no_answer_3";
  /** true lists stage=dead only; false (the default) excludes it (D-32). */
  archived?: boolean;
  sort?: string;
  order?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

type QueryLike = LeadQuery | DealQuery | VendorQuery | Record<string, unknown>;

/** Query values are scalars by construction; an object here would be a caller bug. */
function stringifyParam(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  throw new TypeError(`query parameter is not a scalar: ${typeof value}`);
}

function qs(query: QueryLike): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query as Record<string, unknown>)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      v.forEach((item) => p.append(k, stringifyParam(item)));
    } else {
      p.set(k, stringifyParam(v));
    }
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const api = {
  me: () => request<MeResponse>("/api/v1/me"),
  navCounts: () => request<NavCounts>("/api/v1/nav/counts"),
  overview: () => request<Overview>("/api/v1/overview"),
  leads: (query: LeadQuery = {}) => request<LeadPage>(`/api/v1/leads${qs(query)}`),
  lead: (id: string) => request<LeadDetail>(`/api/v1/leads/${id}`),
  patchLead: (id: string, patch: LeadUpdate) =>
    request<LeadDetail>(`/api/v1/leads/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  editLeadFields: (id: string, edit: LeadEdit) =>
    request<LeadDetail>(`/api/v1/leads/${id}/fields`, {
      method: "PATCH",
      body: JSON.stringify(edit),
    }),
  resolveConflict: (id: string, body: ConflictResolution) =>
    request<LeadDetail>(`/api/v1/leads/${id}/conflicts/resolve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  reviveLead: (id: string) =>
    request<LeadDetail>(`/api/v1/leads/${id}/revive`, { method: "POST" }),
  // v2.6 P3: "Snooze to Friday sweep" and its 5-second undo.
  sweepLead: (id: string) => request<LeadDetail>(`/api/v1/leads/${id}/sweep`, { method: "POST" }),
  unsweepLead: (id: string) => request<LeadDetail>(`/api/v1/leads/${id}/unsweep`, { method: "POST" }),
  // v2.6 P2: the queue rules (D-97, D-98).
  rulesDryRun: () => request<RulesDryRun>("/api/v1/audit/rules/dry-run"),
  ruleActions: (includeDryRun = false) =>
    request<RuleActionRow[]>(`/api/v1/audit/rules/actions?include_dry_run=${includeDryRun}`),
  undoRuleAction: (id: string) =>
    request<Record<string, string>>(`/api/v1/audit/rules/actions/${id}/undo`, { method: "POST" }),
  touchLead: (id: string) =>
    request<LeadDetail>(`/api/v1/leads/${id}/touch`, { method: "POST" }),
  snoozeLead: (id: string, days: 1 | 3 | 7) =>
    request<LeadDetail>(`/api/v1/leads/${id}/snooze`, {
      method: "POST",
      body: JSON.stringify({ days }),
    }),
  search: (q: string) =>
    request<SearchHit[]>(`/api/v1/search?q=${encodeURIComponent(q)}`),
  createTask: (body: TaskCreate) =>
    request<TaskRow>("/api/v1/tasks", { method: "POST", body: JSON.stringify(body) }),
  updateTask: (id: string, body: TaskUpdate) =>
    request<TaskRow>(`/api/v1/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  createNote: (body: NoteCreate) =>
    request<NoteRow>("/api/v1/notes", { method: "POST", body: JSON.stringify(body) }),
  // v2.2 P2: edit, soft-delete, and undo (PATCH deleted_at null).
  updateNote: (id: string, body: NoteUpdate) =>
    request<NoteRow>(`/api/v1/notes/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteNote: (id: string) => request<void>(`/api/v1/notes/${id}`, { method: "DELETE" }),
  // v2.3 P1.1: offers (D-74). sent/countered moves an intake/follow_up lead to offer (D-31).
  offers: (leadId: string) => request<OfferRow[]>(`/api/v1/leads/${leadId}/offers`),
  createOffer: (leadId: string, body: OfferCreate) =>
    request<OfferRow>(`/api/v1/leads/${leadId}/offers`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateOffer: (id: string, body: OfferPatch) =>
    request<OfferRow>(`/api/v1/offers/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteOffer: (id: string) => request<void>(`/api/v1/offers/${id}`, { method: "DELETE" }),

  // ---- §7.5 deals
  deals: (query: DealQuery = {}) => request<DealPage>(`/api/v1/deals${qs(query)}`),
  deal: (id: string) => request<DealDetail>(`/api/v1/deals/${id}`),
  patchDeal: (id: string, patch: DealUpdate) =>
    request<DealDetail>(`/api/v1/deals/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  promoteLead: (leadId: string, body: PromoteRequest) =>
    request<DealDetail>(`/api/v1/leads/${leadId}/promote`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patchChecklistItem: (itemId: string, patch: ChecklistUpdate) =>
    request<DealDetail>(`/api/v1/checklist/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // ---- §7.6 vendors and engagements
  vendors: (query: VendorQuery = {}) => request<VendorRow[]>(`/api/v1/vendors${qs(query)}`),
  vendor: (id: string) => request<VendorRow>(`/api/v1/vendors/${id}`),
  createVendor: (body: VendorUpsert) =>
    request<VendorRow>("/api/v1/vendors", { method: "POST", body: JSON.stringify(body) }),
  updateVendor: (id: string, body: VendorUpsert) =>
    request<VendorRow>(`/api/v1/vendors/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  engagements: (dealId?: string) =>
    request<EngagementRow[]>(`/api/v1/engagements${dealId ? `?deal_id=${dealId}` : ""}`),
  // v2.1 T6.2: a lead's own engagements, before there is a deal.
  leadEngagements: (leadId: string) =>
    request<EngagementRow[]>(`/api/v1/engagements?lead_id=${leadId}`),
  createEngagement: (body: EngagementCreate) =>
    request<EngagementRow>("/api/v1/engagements", { method: "POST", body: JSON.stringify(body) }),
  patchEngagement: (id: string, body: EngagementUpdate) =>
    request<EngagementRow>(`/api/v1/engagements/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  attachDeliverable: (engagementId: string, documentId: string) =>
    request<EngagementRow>(`/api/v1/engagements/${engagementId}/deliverables/${documentId}`, {
      method: "POST",
    }),

  // ---- §7.7 capital
  investors: () => request<InvestorRow[]>("/api/v1/investors"),
  buyers: () => request<BuyerRow[]>("/api/v1/buyers"),
  // v2.1 T6.1: the directory's write half.
  createInvestor: (body: InvestorUpsert) =>
    request<InvestorRow>("/api/v1/investors", { method: "POST", body: JSON.stringify(body) }),
  updateInvestor: (id: string, body: InvestorUpsert) =>
    request<InvestorRow>(`/api/v1/investors/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  createBuyer: (body: BuyerUpsert) =>
    request<BuyerRow>("/api/v1/buyers", { method: "POST", body: JSON.stringify(body) }),
  updateBuyer: (id: string, body: BuyerUpsert) =>
    request<BuyerRow>(`/api/v1/buyers/${id}`, { method: "PUT", body: JSON.stringify(body) }),

  // ---- documents
  uploadUrl: (body: UploadUrlRequest) =>
    request<UploadUrlResponse>("/api/v1/documents/upload-url", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  createDocument: (body: DocumentCreate) =>
    request<DocumentRow>("/api/v1/documents", { method: "POST", body: JSON.stringify(body) }),
  patchParcel: (parcelId: string, body: ParcelDiligenceUpdate) =>
    request<{ id: string; updated: string[] }>(`/api/v1/parcels/${parcelId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  metrics: (q: { days?: number; from?: string; to?: string; market?: string } = {}) =>
    request<Metrics>(`/api/v1/metrics${qs(q)}`),
  markets: () => request<string[]>("/api/v1/markets"),
  events: (q: Record<string, unknown> = {}) =>
    request<EventPage>(`/api/v1/events${qs(q)}`),
  agentRuns: (q: { page?: number; page_size?: number } = {}) =>
    request<AgentRunPage>(`/api/v1/agent-runs${qs(q)}`),
  ingestLog: (q: Record<string, unknown> = {}) =>
    request<IngestLogPage>(`/api/v1/ingest/log${qs(q)}`),
  mapKey: (body: MapKeyRequest) =>
    request<{ source_key: string; normalized_key: string; target: string }>(
      "/api/v1/ingest/map-key",
      { method: "POST", body: JSON.stringify(body) },
    ),
  reprocess: (logId: string) =>
    request<IngestResponse>(`/api/v1/ingest/log/${logId}/reprocess`, { method: "POST" }),
  downloadUrl: (documentId: string) =>
    request<{ url: string; expires_in: number }>(
      `/api/v1/documents/${documentId}/download-url`,
    ),

  // ---- §8 agent layer
  agentBudget: () => request<AgentBudget>("/api/v1/agent/budget"),
  suggestion: (leadId: string) =>
    request<SuggestionResponse>(`/api/v1/agent/leads/${leadId}/suggestion`),
  acceptSuggestion: (leadId: string) =>
    request<LeadDetail>(`/api/v1/agent/leads/${leadId}/suggestion/accept`, { method: "POST" }),
  // v2.3 P1.2: accept one money value from a pending suggestion (D-75).
  applySuggestionField: (leadId: string, body: SuggestionFieldApply) =>
    request<LeadDetail>(`/api/v1/agent/leads/${leadId}/suggestion/apply-field`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  rejectSuggestion: (leadId: string, reason?: string) =>
    request<LeadDetail>(`/api/v1/agent/leads/${leadId}/suggestion/reject`, {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? null }),
    }),
  triageLeadsBulk: (leadIds: string[]) =>
    request<BulkTriageResponse>("/api/v1/agent/leads/triage-bulk", {
      method: "POST",
      body: JSON.stringify({ lead_ids: leadIds }),
    }),
  triageLead: (leadId: string) =>
    request<JobRunResponse>(`/api/v1/agent/leads/${leadId}/triage`, { method: "POST" }),
  ddSnapshot: (parcelId: string) =>
    request<JobRunResponse>(`/api/v1/agent/parcels/${parcelId}/dd-snapshot`, { method: "POST" }),
  underwriteSuggest: (dealId: string) =>
    request<JobRunResponse>(`/api/v1/agent/deals/${dealId}/underwrite`, { method: "POST" }),
  rfqDraft: (engagementId: string) =>
    request<JobRunResponse>(`/api/v1/agent/engagements/${engagementId}/rfq-draft`, {
      method: "POST",
    }),
  ask: (question: string) =>
    request<AskResponse>("/api/v1/agent/ask", {
      method: "POST",
      body: JSON.stringify({ question }),
    }),

  // ---- the trust ladder (D-33)
  autoApplied: (leadId?: string) =>
    request<AutoApplication[]>(
      `/api/v1/agent/auto-applied${leadId ? `?lead_id=${encodeURIComponent(leadId)}` : ""}`,
    ),
  undoAutoApplied: (applicationId: string) =>
    request<LeadDetail>(`/api/v1/agent/auto-applied/${applicationId}/undo`, { method: "POST" }),
  acceptAllSuggestions: (threshold?: string) =>
    request<AcceptAllResponse>(
      `/api/v1/agent/suggestions/accept-all${threshold ? `?threshold=${threshold}` : ""}`,
      { method: "POST" },
    ),
  trustStandings: () => request<TrustStanding[]>("/api/v1/agent/trust"),
};

export interface DealQuery {
  q?: string;
  stage?: string[];
  strategy?: string[];
  kill?: boolean;
  sort?: string;
  order?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

export interface VendorQuery {
  q?: string;
  type?: string[];
  state?: string;
  county?: string;
  active?: boolean;
  min_rating?: number;
  service?: string;
}

/**
 * §9 Phase 3: upload straight to Supabase Storage with a signed URL, then tell the API what
 * landed. File bytes never pass through the API, and the browser never holds the service key.
 */
export async function uploadDocument(
  file: File,
  target: { entity_type: UploadUrlRequest["entity_type"]; entity_id: string; kind: DocumentCreate["kind"] },
): Promise<DocumentRow> {
  const signed = await api.uploadUrl({
    entity_type: target.entity_type,
    entity_id: target.entity_id,
    filename: file.name,
    kind: target.kind as UploadUrlRequest["kind"],
  });

  const put = await fetch(signed.upload_url, {
    method: "PUT",
    headers: {
      ...(signed.token ? { Authorization: `Bearer ${signed.token}` } : {}),
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });
  if (!put.ok) {
    throw new ApiError(put.status, `upload failed: ${put.status} ${put.statusText}`);
  }

  return api.createDocument({
    entity_type: target.entity_type,
    entity_id: target.entity_id,
    storage_path: signed.storage_path,
    filename: file.name,
    kind: target.kind,
    mime: file.type || null,
    notes: null,
  });
}
